// angular imports
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';

// devextreme imports
import { DxDataGridComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';

// 3rd party
import { forkJoin } from 'rxjs';

// cc-common imports
import { MessageBoxComponent, IconConstants, Utils } from 'cc-common';

// service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { WorkflowCamStepService } from '../../_services/workflow-cam-step.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';

// model imports
import { WFEBudgetedGLAccountSelectorResultDto } from '../../../shared-workflow-module/_models/wfe-budgeted-gl-account-selector-result-dto';
import { DimensionSelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/dimensions/dimension-selector-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/subcategory-by-gl-account-parametere-dto';
import { WFEWorkflowItemRawDto } from '../../_models/workflow-data-entry/wfe-workflow-item-raw-dto';
import { GLSubcategorySelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { WFEBankAccountResultDto } from '../../../shared-workflow-module/_models/wfe-bank-account-result-dto';
import { WFEBudgetedGLAccountSelectorParameterDto } from '../../../shared-workflow-module/_models/wfe-budgeted-gl-account-selector-parameter-dto';
import { WFEVendorParametersDto } from '../../_models/common/wfe-vendor-parameter-dto';
import { WFEVendorTemplateResultDto } from '../../_models/workflow-data-entry/wfe-vendor-template-result-dto';
import { WFEVendorAccountParametersDto } from '../../_models/workflow-data-entry/wfe-vendor-account-parameters-dto';
import { WFEVendorAccountTemplateResultDto } from '../../_models/workflow-data-entry/wfe-vendor-account-template-result-dto';
import { VendorLineItemTemplateRawDto } from '../../../shared-workflow-module/_models/vendor-lineItem-template-raw-dto';
import { WFECommunityResultDto } from '../../../shared-workflow-module/_models/wfe-community-result-dto';

// constants imports
import { ActionConstants } from '../../../_constants/action-constants';

@Component({
    selector: 'app-workflow-step-line-item-editor',
    templateUrl: './workflow-step-line-item-editor.component.html',
    styleUrls: ['./workflow-step-line-item-editor.component.scss']
})
export class WorkflowStepLineItemEditorComponent implements OnInit, OnChanges {
    @ViewChild('lineItemGrid', { static: false }) lineItemGrid: DxDataGridComponent;
    @ViewChild('lineItemEditorMessageBox', { static: false })
    lineItemEditorMessageBox: MessageBoxComponent;

    @Input() lineItems: Array<WFEWorkflowItemRawDto> = [];
    @Input() glAccountsBudgetInfo: Array<WFEBudgetedGLAccountSelectorResultDto> = [];
    @Input() dimensionSource: Array<DimensionSelectorResultDto> = [];
    @Input() managementCompanyId: number;
    @Input() communityId: number;
    @Input() invoiceAmount: number;
    @Input() entityUsesSubCategory: boolean;
    @Input() matchEntityRequiredDimensions: boolean;
    @Input() selectedBankAccount: WFEBankAccountResultDto;
    @Input() targetCommunityList: Array<WFECommunityResultDto> = [];
    @Input() description: string;
    @Input() payeeCode: string;
    @Input() vendorAccountId: number;
    @Input() workflowId: number;
    @Input() workflowStatus: string;
    @Input() allowEditInvoiceHeaderDetails = true; // This flag is only coming for approved step, hence by default it's true

    gridLineItems: Array<WFEWorkflowItemRawDto> = [];
    modifiedLineItems: Array<WFEWorkflowItemRawDto> = [];
    defaultLineItems: Array<VendorLineItemTemplateRawDto> = [];

    glAccountColumns: Array<Column> = [];
    subCategoryColumns: Array<Column> = [];
    communityColumns: Array<Column> = [];

    loadingVisible = false;

    currencyFormat: string;

    iconPlus = IconConstants.ADD;
    iconDelete = IconConstants.DELETE;
    iconArrowDown = IconConstants.ARROW_DOWN_CIRCLE;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly workflowCamStepService: WorkflowCamStepService,
        private readonly workflowEditorService: WorkflowEditorService
    ) {
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit() {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.prepareColumns();
        if (Utils.notNullOrEmpty(this.lineItems)) {
            this.prepareLineItems(this.lineItems);
        } else {
            this.addRawLineItem();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedBankAccount && changes.selectedBankAccount.currentValue) {
            this.selectedBankAccount = changes.selectedBankAccount.currentValue;
            this.updateLineItemsRequiredDimensions();
        }

        if (changes.description) {
            this.description = changes.description.currentValue;
            this.updateDescription();
        }
    }

    onRowUpdated(event: any): void {
        const rowData = event.data;
        this.gridLineItems.forEach((item) => {
            if (Utils.equals(item['itemId'], rowData['itemId'])) {
                if (Utils.isNullOrEmpty(item.crudAction)) {
                    item.crudAction = ActionConstants.EDIT;
                }
            }
        });
    }

    onAddNewLineItem(): void {
        this.addRawLineItem();
    }

    onDefaultLineItems(): void {
        if (Utils.notNullOrEmpty(this.gridLineItems)) {
            const dialog = this.helperService.getConfirmationDialog(
                `Confirmation`,
                `There are line item changes. Would you like to continue by setting default line items?`,
                'Yes',
                'No'
            );
            dialog.show().then((answer: boolean) => {
                if (Utils.isTrue(answer)) {
                    if (Utils.notNullAndDefined(this.vendorAccountId)) {
                        this.populateVendorAccountLineItemTemplates(true);
                    } else {
                        this.populateVendorLineItemTemplates(true);
                    }
                }
            });
        } else if (Utils.notNullAndDefined(this.vendorAccountId)) {
            this.populateVendorAccountLineItemTemplates(true);
        } else {
            this.populateVendorLineItemTemplates(true);
        }
    }

    onDeleteLineItemClick(item: WFEWorkflowItemRawDto): void {
        if (!item.apBillItemId) {
            this.gridLineItems = this.gridLineItems.filter((d) => Utils.notEquals(d['itemId'], item['itemId']));
        } else {
            this.gridLineItems.forEach((ele) => {
                if (Utils.equals(ele.apBillItemId, item.apBillItemId)) {
                    ele.crudAction = ActionConstants.DELETE;
                    const originalItem = this.lineItems.find((d) => Utils.equals(d.apBillItemId, item.apBillItemId));
                    if (Utils.notNullAndDefined(originalItem)) {
                        item.itemAmount = originalItem.itemAmount;
                        item.description = originalItem.description;
                        item.glAccountNumber = originalItem.glAccountNumber;
                        item.subCategoryId = originalItem.subCategoryId;
                        item.targetCommunityId = originalItem.targetCommunityId;
                        item.dimensions = originalItem.dimensions;
                    }
                }
            });
        }

        this.lineItemGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.lineItemGrid.instance.refresh();
    }

    onGLAccountSelection(account: WFEBudgetedGLAccountSelectorResultDto[], cellInfo: any): void {
        if (Utils.notNullOrEmpty(account)) {
            if (Utils.isTrue(this.entityUsesSubCategory)) {
                this.populateSubcategories(account[0].glAccountNumber, cellInfo.data);
            }

            this.populateDimensions(account[0].glAccountNumber, cellInfo.data);
        }
    }

    onTargetCommunitySelection(selectedRow: WFECommunityResultDto[], cellInfo: any): void {
        if (Utils.notNullOrEmpty(selectedRow) && Utils.notEquals(cellInfo.value, cellInfo.data.targetCommunityId)) {
            this.populateGLAccounts(selectedRow[0].communityId, cellInfo.data);
        }
    }

    onSubCategorySelection(subCategory: GLSubcategorySelectorResultDto[], cellInfo: any): void {
        if (Utils.notNullOrEmpty(subCategory)) {
            cellInfo.setValue(subCategory[0].id);
        } else {
            cellInfo.setValue(null);
        }
    }

    onDimensionValueChanged(event: any, cellInfo: any): void {
        cellInfo.setValue(event.value);
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    private populateGLAccounts(communityId: number, rawItem: WFEWorkflowItemRawDto): void {
        const baseParams: WFEBudgetedGLAccountSelectorParameterDto = new WFEBudgetedGLAccountSelectorParameterDto();
        baseParams.communityId = communityId;
        baseParams.bankAccountId =
            Utils.notNullAndDefined(this.selectedBankAccount) &&
            Utils.notNullAndDefined(this.selectedBankAccount.bankAccountId)
                ? this.selectedBankAccount.bankAccountId
                : null;

        this.workflowCamStepService.getGLAccounts(baseParams).subscribe(
            (result: WFEBudgetedGLAccountSelectorResultDto[]) => {
                rawItem['glAccountsBudgetInfo'] = result;
                rawItem.glAccountNumber = null;

                rawItem['subCategorySource'] = [];
                rawItem.subCategoryId = null;

                rawItem['dimensionList'].forEach((dimItem) => {
                    delete rawItem[`${dimItem.code}_source`];
                    delete rawItem[`${dimItem.code}_required`];
                });
                rawItem['dimensionList'] = [];
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private populateSubcategories(glAccountNumber: string, rawItem: WFEWorkflowItemRawDto, clearCache = false): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = this.managementCompanyId;
        baseParam.communityId = !!rawItem.targetCommunityId ? rawItem.targetCommunityId : this.communityId;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.workflowId = this.workflowId;
        baseParam.clearCache = clearCache;
        this.workflowCamStepService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                rawItem['subCategorySource'] = result;

                const subCategory = result.find((d) => Utils.equals(d.id, rawItem.subCategoryId));
                rawItem.subCategoryId = Utils.notNullAndDefined(subCategory) ? subCategory.id : null;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private populateDimensions(glAccountNumber: string, lineItem: WFEWorkflowItemRawDto): void {
        const baseParams: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParams.communityId = !!lineItem.targetCommunityId ? lineItem.targetCommunityId : this.communityId;
        baseParams.managementCompanyId = this.managementCompanyId;
        baseParams.glAccountNumber = glAccountNumber;

        this.workflowCamStepService.getDimensions(baseParams).subscribe(
            (result: DimensionSelectorResultDto[]) => {
                lineItem['dimensionList'] = result;
                this.prepareLineItemDimensions(lineItem, result);

                setTimeout(() => {
                    if (Utils.notNullOrEmpty(lineItem.dimensions)) {
                        const lineItemDimensionsList = lineItem.dimensions.split(','); // eg: 'FUND|OPER,' or 'FUND|OPER,OWNER_TYPE|B'
                        const lineItemDimensions = lineItemDimensionsList.map((m) => {
                            const dimensionOptionsCodes = m.split('|');
                            return {
                                dimCode: dimensionOptionsCodes[0],
                                optionCode: dimensionOptionsCodes[1]
                            };
                        }); // eg: [[{ dimCode: FUND, optionCode: OPER}, { dimCode: OWNER_TYPE, optionCode: B}]
                        lineItem['dimensionList'].forEach((glAccountDimension) => {
                            const dimCode = glAccountDimension.code; // eg: FUND
                            let dimOptionCode = null;
                            const lineItemDimensionOption = lineItemDimensions.find((d) =>
                                Utils.equalsIgnoreCase(dimCode, d.dimCode)
                            ); // eg: d = { dimCode: FUND, optionCode: OPER} will return { dimCode: FUND, optionCode: OPER}
                            if (Utils.notNullAndDefined(lineItemDimensionOption)) {
                                dimOptionCode = lineItemDimensionOption.optionCode; // eg: OPER
                            }

                            if (
                                Utils.isTrue(lineItem[`${dimCode}_required`]) &&
                                Utils.isTrue(this.matchEntityRequiredDimensions) &&
                                Utils.notNullAndDefined(this.selectedBankAccount) &&
                                Utils.notNullOrEmpty(this.selectedBankAccount.glDimensions)
                            ) {
                                this.updateLineItemDimensionWithBankAccountDimension(dimCode, lineItem);
                            } else {
                                lineItem[dimCode] = dimOptionCode;
                            }
                        });
                    } else {
                        if (
                            Utils.isTrue(this.matchEntityRequiredDimensions) &&
                            Utils.notNullAndDefined(this.selectedBankAccount) &&
                            Utils.notNullOrEmpty(this.selectedBankAccount.glDimensions)
                        ) {
                            lineItem['dimensionList'].forEach((dim) => {
                                if (Utils.isTrue(dim.required)) {
                                    this.updateLineItemDimensionWithBankAccountDimension(dim.code, lineItem);
                                }
                            });
                        }
                    }
                }, 100);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private updateLineItemDimensionWithBankAccountDimension(
        dimensionCode: string,
        lineItem: WFEWorkflowItemRawDto
    ): void {
        const selectedBankAccountDimension = this.selectedBankAccount.glDimensions.find((d) =>
            Utils.equalsIgnoreCase(d.dimensionCode, dimensionCode)
        );
        if (Utils.notNullAndDefined(selectedBankAccountDimension)) {
            const selectedDimensionOption = lineItem[`${dimensionCode}_source`].find((d) =>
                Utils.equalsIgnoreCase(d.code, selectedBankAccountDimension.dimensionOptionCode)
            );
            lineItem[dimensionCode] = Utils.notNullAndDefined(selectedDimensionOption)
                ? selectedDimensionOption.code
                : null;
        }
    }

    private prepareLineItems(lineItems: WFEWorkflowItemRawDto[]): void {
        const gridItems: Array<WFEWorkflowItemRawDto> = [];
        lineItems.forEach((lineItem) => {
            let obj = new WFEWorkflowItemRawDto();

            obj['itemId'] = lineItems.indexOf(lineItem);
            obj['targetCommunityId'] = Utils.notNullOrEmpty(lineItem.targetCommunityId)
                ? lineItem.targetCommunityId
                : null;
            obj['glAccountsBudgetInfo'] = this.glAccountsBudgetInfo;

            obj['dimensionList'] = this.dimensionSource;
            this.prepareLineItemDimensions(obj, this.dimensionSource);

            obj = Object.assign(obj, lineItem);

            obj.crudAction = '';

            this.populateDimensions(obj.glAccountNumber, obj);

            if (Utils.isTrue(this.entityUsesSubCategory)) {
                this.populateSubcategories(obj.glAccountNumber, obj);
            }
            gridItems.push(obj);
        });
        this.gridLineItems = gridItems;
    }

    private prepareLineItemDimensions(
        lineItem: WFEWorkflowItemRawDto,
        dimensionSource: Array<DimensionSelectorResultDto>
    ): void {
        dimensionSource.forEach((dim) => {
            lineItem[`${dim.code}_required`] = dim.required;
            lineItem[`${dim.code}_source`] = dim.options;
        });
    }

    private updateLineItemsRequiredDimensions(): void {
        if (
            Utils.isTrue(this.matchEntityRequiredDimensions) &&
            Utils.notNullAndDefined(this.selectedBankAccount) &&
            Utils.notNullOrEmpty(this.selectedBankAccount.glDimensions)
        ) {
            this.dimensionSource.forEach((dim) => {
                if (Utils.isTrue(dim.required)) {
                    const selectedBankAccountDimension = this.selectedBankAccount.glDimensions.find((d) =>
                        Utils.equalsIgnoreCase(d.dimensionCode, dim.code)
                    );
                    if (Utils.notNullAndDefined(selectedBankAccountDimension)) {
                        const selectedDimensionOption = dim.options.find((d) =>
                            Utils.equalsIgnoreCase(d.code, selectedBankAccountDimension.dimensionOptionCode)
                        );
                        if (Utils.notNullAndDefined(selectedDimensionOption)) {
                            this.gridLineItems.forEach((lineItem) => {
                                lineItem[`${dim.code}_source`] = dim.options;
                                setTimeout(() => {
                                    lineItem[dim.code] = selectedDimensionOption.code;
                                }, 100);
                            });
                        }
                    }
                }
            });
        }
    }

    private updateDescription(): void {
        this.gridLineItems.forEach((lineItem) => {
            lineItem.description = this.description;
        });
    }

    private addRawLineItem(): void {
        const item: WFEWorkflowItemRawDto = new WFEWorkflowItemRawDto();
        item.itemAmount = this.precisionService.getRoundedNumber(this.invoiceAmount);
        item['itemId'] = Utils.notNullOrEmpty(this.gridLineItems)
            ? Math.max.apply(
                  Math,
                  this.gridLineItems.map((o) => {
                      return o['itemId'];
                  })
              ) + 1
            : 1;
        item.crudAction = ActionConstants.ADD;
        item.apBillItemId = null;
        item.description = this.description;
        item['dimensionList'] = this.dimensionSource;

        item['targetCommunityId'] = null;
        item['glAccountsBudgetInfo'] = this.glAccountsBudgetInfo;

        this.gridLineItems.push(item);

        this.updateLineItemsRequiredDimensions();
    }

    private validateRawItem(rawItem: WFEWorkflowItemRawDto): any {
        const resultStatus = { isValid: true, message: null };
        if (Utils.notEqualsIgnoreCase(rawItem.crudAction, ActionConstants.DELETE)) {
            if (rawItem.itemAmount === 0 || Utils.isNullOrUndefined(rawItem.itemAmount)) {
                resultStatus.isValid = false;
                resultStatus.message = 'Line Item Amount must be different from zero.';
            }
            if (!rawItem.glAccountNumber) {
                resultStatus.isValid = false;
                resultStatus.message = 'GL Account Number cannot be empty.';
            }
            if (Utils.isNullOrEmpty(rawItem.dimensions)) {
                resultStatus.isValid = false;
                resultStatus.message = 'Dimensions cannot be empty.';
            }
        }
        return resultStatus;
    }

    public getModifiedLineItems(): WFEWorkflowItemRawDto[] {
        return this.modifiedLineItems;
    }

    public isValidLineItems(): boolean {
        let isValid = true;
        const dimensionKeysToDelete: string[] = [];
        this.modifiedLineItems = [];
        let totalAmount = 0;

        if (Utils.notNullOrEmpty(this.gridLineItems)) {
            for (const lineItem of this.gridLineItems) {
                const rawItem = Object.assign(new WFEWorkflowItemRawDto(), lineItem);

                if (Utils.notNullOrEmpty(rawItem['dimensionList'])) {
                    rawItem.dimensions = '';

                    rawItem['dimensionList'].forEach((dim) => {
                        dimensionKeysToDelete.push(dim.code);
                        dimensionKeysToDelete.push(`${dim.code}_source`);
                        dimensionKeysToDelete.push(`${dim.code}_required`);
                        if (Utils.notNullAndDefined(dim.code) && Utils.notNullOrEmpty(rawItem[dim.code])) {
                            if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                                rawItem.dimensions += ',';
                            }
                            rawItem.dimensions += `${dim.code}|${rawItem[dim.code]}`;
                        }
                    });
                }

                const resultStatus = this.validateRawItem(rawItem);
                isValid = resultStatus.isValid;
                if (Utils.isTrue(resultStatus.isValid)) {
                    totalAmount += Utils.notEqualsIgnoreCase(rawItem.crudAction, ActionConstants.DELETE)
                        ? rawItem.itemAmount
                        : 0;
                    dimensionKeysToDelete.forEach((key) => {
                        delete rawItem[key];
                    });
                    delete rawItem['subCategorySource'];
                    delete rawItem['dimensionList'];
                    delete rawItem['isNewlyAdded'];
                    delete rawItem['targetCommunityList'];
                    delete rawItem['glAccountsBudgetInfo'];
                } else {
                    this.lineItemEditorMessageBox.showErrorMessage('Error', resultStatus.message);
                    this.modifiedLineItems = [];
                    break;
                }
                this.modifiedLineItems.push(rawItem);
            }
            if (Utils.isTrue(isValid)) {
                totalAmount = this.precisionService.getRoundedNumber(totalAmount);
                const invoiceAmount = this.precisionService.getRoundedNumber(this.invoiceAmount);
                if (totalAmount !== invoiceAmount) {
                    this.lineItemEditorMessageBox.showErrorMessage(
                        'Error',
                        'The sum of the Line Item Amount(s) must equal to the Amount To Pay.'
                    );
                    this.modifiedLineItems = [];
                    isValid = false;
                }
            }
        }

        return isValid;
    }

    private populateVendorLineItemTemplates(clearCache = false): void {
        const baseParams: WFEVendorParametersDto = new WFEVendorParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workflowStatus;
        baseParams.vendorCode = this.payeeCode;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;

        this.workflowEditorService.getVendorLineTemplate(baseParams).subscribe(
            (result: WFEVendorTemplateResultDto) => {
                // Handling loading panel visibility inside prepareLineItemTemplates method.
                this.prepareLineItemTemplates(result);
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateVendorAccountLineItemTemplates(clearCache = false): void {
        const baseParams: WFEVendorAccountParametersDto = new WFEVendorAccountParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workflowStatus;
        baseParams.vendorCode = this.payeeCode;
        baseParams.vendorAccountId = this.vendorAccountId;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getVendorAccountLineTemplate(baseParams).subscribe(
            (result: WFEVendorAccountTemplateResultDto) => {
                // As per ANG-2327, Confirmed with Sandeep. Below functionality is different from V1.
                // If active vendor account template line items are not available, then use active vendor template line items.
                const activeLineItemTemplates = result.lineItemTemplates.filter((d) => Utils.isTrue(d.enabled));
                if (Utils.isNullOrEmpty(activeLineItemTemplates)) {
                    this.populateVendorLineItemTemplates(true);
                } else {
                    // Handling loading panel visibility inside prepareLineItemTemplates method.
                    this.prepareLineItemTemplates(result);
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareLineItemTemplates(result: WFEVendorTemplateResultDto | WFEVendorAccountTemplateResultDto): void {
        /* Note: As per ANG-2327, This functionality is different from V1.
            If active template line items are available
                - if template lineItem GL account is available in workflow GL account list
                    -- if entityUsesSubcategory is true, then prepare subcategory list, also validate template lineItem subcateogory in WF subcategory list. If template subcategory is null or valid, mark the lineItem as valid
                    -- if entityUsesSubcategory is false, then mark the template lineItem as valid
                - else show message as 'Template GL Account is invalid'

            Additionally, if WF and template line items count is 1,then 
            use WF itemAmount if template lineItem itemAmount is not available.
            use WF description if template lineItem description is not available
        */

        const activeTemplateLineItems = result.lineItemTemplates.filter((d) => Utils.isTrue(d.enabled));
        const templateLineItems: Array<any> = []; // this will hold valid template lineItems
        let messages: Array<{ message: string; level: number }> = []; // this will hold all messages

        if (Utils.notNullOrEmpty(activeTemplateLineItems)) {
            if (Utils.isTrue(this.entityUsesSubCategory)) {
                activeTemplateLineItems.forEach((lineItem) => {
                    // check if coding default line item gl account exist in workflow line item glaccount list
                    // If template GL Account found in WF GL Account list, check if template subcategory availability in populateSubcategories method
                    const glAccountInfo = this.glAccountsBudgetInfo.find((d) =>
                        Utils.equals(d.subAccountId, lineItem.subAccountId)
                    );
                    if (Utils.notNullAndDefined(glAccountInfo) && Utils.notNullOrEmpty(glAccountInfo.glAccountNumber)) {
                        lineItem['glAccountNumber'] = glAccountInfo.glAccountNumber;

                        // preparing subcategories as entityUsesSubcategory is true
                        const baseParam: SubCategoryByGlAccountParametersDto =
                            new SubCategoryByGlAccountParametersDto();
                        baseParam.managementCompanyId = this.managementCompanyId;
                        baseParam.communityId = this.communityId;
                        baseParam.glAccountNumber = glAccountInfo.glAccountNumber;
                        baseParam.workflowId = this.workflowId;
                        baseParam.clearCache = false;
                        lineItem['subCategoryAPI'] = this.workflowCamStepService.getSubCategories(baseParam);
                        templateLineItems.push(lineItem);
                    } else {
                        messages.push({
                            message: `Template GL Account ${lineItem.subAccountId} is not available in Workflow GL Accounts`,
                            level: 2
                        });
                    }
                });
                if (Utils.notNullOrEmpty(templateLineItems)) {
                    const defaultLineItems: Array<VendorLineItemTemplateRawDto> = [];
                    forkJoin([...templateLineItems.map((lineItem) => lineItem['subCategoryAPI'])]).subscribe(
                        (results) => {
                            templateLineItems.forEach((lineItem, index) => {
                                lineItem['subCategorySource'] = results[index];
                                if (Utils.notNullOrEmpty(lineItem.subCategoryId)) {
                                    const subCategory = lineItem['subCategorySource'].find((subCat) =>
                                        Utils.equals(subCat.id, lineItem.subCategoryId)
                                    );
                                    // As per ANG-2327, This functionality is different than V1
                                    // If template line item subcategory is available in WF subcategories list, then only use it as valid template line item.
                                    if (Utils.notNullAndDefined(subCategory)) {
                                        defaultLineItems.push(lineItem);
                                    } else {
                                        messages.push({
                                            message: `Template Subcategory ${lineItem.subCategoryId} of GL Account ${lineItem['glAccountNumber']} is not available in Workflow GL Account's Subcategories.`,
                                            level: 2
                                        });
                                    }
                                } else {
                                    defaultLineItems.push(lineItem);
                                }
                            });

                            if (Utils.notNullOrEmpty(defaultLineItems)) {
                                this.defaultLineItems = defaultLineItems;
                                this.setDefaultLineItems();
                            } else {
                                messages = [
                                    {
                                        message: 'No active line items available for workflow for following reasons: ',
                                        level: 1
                                    }
                                ].concat(messages);
                            }
                            this.showMessages(messages, activeTemplateLineItems.length, defaultLineItems.length);
                            this.loadingVisible = false;
                        }
                    );
                } else {
                    messages = [
                        { message: 'No active line items available for workflow for following reasons: ', level: 1 }
                    ].concat(messages);
                    this.showMessages(messages, activeTemplateLineItems.length, templateLineItems.length);
                    this.loadingVisible = false;
                }
            } else {
                activeTemplateLineItems.forEach((lineItem) => {
                    // check if coding default line item gl account exist in workflow line item glaccount list
                    // If template GL Account found in WF GL Account list, check if template subcategory availability in populateSubcategories method
                    const glAccountInfo = this.glAccountsBudgetInfo.find((d) =>
                        Utils.equals(d.subAccountId, lineItem.subAccountId)
                    );
                    if (Utils.notNullAndDefined(glAccountInfo) && Utils.notNullOrEmpty(glAccountInfo.glAccountNumber)) {
                        lineItem['glAccountNumber'] = glAccountInfo.glAccountNumber;
                        templateLineItems.push(lineItem);
                    } else {
                        messages.push({
                            message: `Template GL Account ${lineItem.subAccountId} is not available in Workflow GL Accounts`,
                            level: 2
                        });
                    }
                });

                if (Utils.notNullOrEmpty(templateLineItems)) {
                    this.defaultLineItems = templateLineItems;
                    this.setDefaultLineItems();
                } else {
                    messages = [
                        { message: 'No active line items available for workflow for following reasons: ', level: 1 }
                    ].concat(messages);
                }
                this.showMessages(messages, activeTemplateLineItems.length, templateLineItems.length);
                this.loadingVisible = false;
            }
        } else {
            messages = [{ message: 'No active line items available for workflow', level: 1 }];
            this.showMessages(messages, activeTemplateLineItems.length, templateLineItems.length);
            this.loadingVisible = false;
        }
    }

    private showMessages(
        messages: Array<{ message: string; level: number }>,
        activeLineItemCount: number,
        defaultLineItemCount: number
    ): void {
        if (activeLineItemCount > defaultLineItemCount || Utils.equals(activeLineItemCount, 0)) {
            let headMessage: string = null;
            const bulletPoints: string[] = [];

            messages.forEach((msg) => {
                if (Utils.equals(msg.level, 1)) {
                    headMessage = `${msg.message}<br>`;
                } else {
                    bulletPoints.push(msg.message);
                }
            });

            if (Utils.isNullOrEmpty(headMessage) && Utils.notNullOrEmpty(bulletPoints)) {
                const missedLineItemCount = activeLineItemCount - defaultLineItemCount;
                headMessage = `${missedLineItemCount} line item(s) is not considered as valid line item(s) due to following reasons <br>`;
            }

            const finalMessage = Utils.notNullOrEmpty(bulletPoints)
                ? `${headMessage} <ul> <li>${Utils.uniqueList(bulletPoints).join('</li><li>')}</li></ul>`
                : `${headMessage}`;
            this.lineItemEditorMessageBox.showInfoMessage('Information', finalMessage);
        }
    }

    private setDefaultLineItems(): void {
        const defaultLineItemTemplates: Array<WFEWorkflowItemRawDto> = [];
        this.defaultLineItems.forEach((defaultLineItem, index) => {
            const obj = new WFEWorkflowItemRawDto();
            obj.glAccountNumber = defaultLineItem['glAccountNumber'];
            obj.subCategoryId = defaultLineItem.subCategoryId;
            obj['itemId'] = index;
            obj['targetCommunityId'] = null;
            obj['glAccountsBudgetInfo'] = this.glAccountsBudgetInfo;
            obj['dimensionList'] = this.dimensionSource;
            obj['subCategorySource'] = defaultLineItem['subCategorySource'];
            obj.itemAmount = defaultLineItem.itemAmount;
            obj.description = defaultLineItem.description;
            obj.sequence = defaultLineItem.sequence;
            obj.templateId = defaultLineItem.templateId;
            obj.dimensions = defaultLineItem.dimensions;

            this.prepareLineItemDimensions(obj, this.dimensionSource);
            if (Utils.notNullOrEmpty(obj.glAccountNumber)) {
                this.populateDimensions(obj.glAccountNumber, obj);
            }
            obj.crudAction = ActionConstants.ADD;
            defaultLineItemTemplates.push(obj);
        });

        /* Note: As per ANG-2327, This functionality is different from V1.
            If active template line items are available
                - if template lineItem GL account is available in workflow GL account list
                    -- if entityUsesSubcategory is true, then prepare subcategory list, also validate template lineItem subcateogory in WF subcategory list. If template subcategory is null or valid, mark the lineItem as valid
                    -- if entityUsesSubcategory is false, then mark the template lineItem as valid
                - else show message as 'Template GL Account is invalid'

            Additionally, if WF and template line items count is 1,then 
            use WF itemAmount if template lineItem itemAmount is not available.
            use WF description if template lineItem description is not available
        */
        if (
            Utils.equals(this.gridLineItems.length, defaultLineItemTemplates.length) &&
            Utils.equals(this.gridLineItems.length, 1)
        ) {
            if (Utils.isNullOrEmpty(defaultLineItemTemplates[0].itemAmount)) {
                defaultLineItemTemplates[0].itemAmount = this.gridLineItems[0].itemAmount;
            }
            if (Utils.isNullOrEmpty(defaultLineItemTemplates[0].description)) {
                defaultLineItemTemplates[0].description = this.gridLineItems[0].description;
            }
        }
        this.gridLineItems = defaultLineItemTemplates;
        this.lineItemGrid.instance.refresh();
    }

    private prepareColumns(): void {
        this.glAccountColumns = [
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account No'
            },
            {
                dataField: 'glAccountDisplayText',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'budgetAmount',
                dataType: 'number',
                caption: 'Budget',
                format: this.currencyFormat
            }
        ];

        this.communityColumns = [
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community'
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'managementCompanyName',
                dataType: 'string',
                caption: 'Management Company'
            },
            {
                dataField: 'status',
                dataType: 'string',
                caption: 'Status'
            }
        ];

        this.subCategoryColumns = [
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'budgetAmount',
                dataType: 'number',
                caption: 'Budget',
                format: this.currencyFormat
            }
        ];
    }
}
